import React, { useEffect, useState } from 'react'
import {
	Title,
	Grid,
	Card,
	Text,
	Loader,
	Table,
	Box,
	Select,
	Paper,
} from '@mantine/core'
import { getExperienceStats } from '../services/actions/experienceActions'
import { useParams } from 'react-router-dom'
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from 'recharts'

const ExperienceOverviewPage = () => {
	const { experienceSlug } = useParams()
	const [stats, setStats] = useState({})
	const [loading, setLoading] = useState(true)
	const [selectedStation, setSelectedStation] = useState('Total')

	const fetchStats = async () => {
		try {
			const data = await getExperienceStats(experienceSlug)

			if (Array.isArray(data.checkInsByHour)) {
				data.checkInsByHour = data.checkInsByHour.map(({ hour, count }) => {
					const local = new Date()
					local.setUTCHours(hour, 0, 0, 0)

					const localHour = local.getHours()
					const label = `${localHour % 12 || 12}${localHour < 12 ? 'am' : 'pm'}`

					return { hour: localHour, label, count }
				})
			}

			setStats(data)
		} catch (err) {
			console.error(err)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchStats()
		const interval = setInterval(fetchStats, 30000) // Refresh every 30 seconds
		return () => clearInterval(interval)
	}, [experienceSlug])

	const stationOptions = Object.keys(stats?.itemsByStation || {}).map(
		(station) => ({
			label: station,
			value: station,
		})
	)

	const itemRows =
		stats?.itemsByStation?.[selectedStation]?.map((item) => {
			const remaining = item.totalAttached - item.totalIssued
			const percentComplete =
				item.totalAttached > 0
					? Math.round((item.totalIssued / item.totalAttached) * 100)
					: 0

			return {
				itemName: item.itemName,
				attached: item.totalAttached,
				issued: item.totalIssued,
				remaining,
				percentComplete,
			}
		}) || []

	return (
		<Box p='md'>
			<Title order={2} mb='md'>
				Check-In Overview
			</Title>

			{loading ? (
				<Loader />
			) : Object.keys(stats).length === 0 ? (
				<Text>Error loading stats</Text>
			) : (
				<>
					<Grid>
						<Grid.Col xs={12} md={4}>
							<Card shadow='sm' withBorder>
								<Text size='sm' weight={500}>
									Total Check-Ins Completed
								</Text>
								<Title>{stats.checkInsCompletedTotal}</Title>
							</Card>
						</Grid.Col>
						<Grid.Col xs={12} md={4}>
							<Card shadow='sm' withBorder>
								<Text size='sm' weight={500}>
									Total Check-Ins Remaining
								</Text>
								<Title>{stats.checkInsRemainingTotal}</Title>
							</Card>
						</Grid.Col>
						<Grid.Col xs={12} md={4}>
							<Card shadow='sm' withBorder>
								<Text size='sm' weight={500}>
									% Complete Overall
								</Text>
								<Title>{stats.checkInCompletionRateTotal}%</Title>
							</Card>
						</Grid.Col>
					</Grid>

					{stats.checkInsByHour?.length > 0 && (
						<Paper shadow='xs' p='md' withBorder mb='md' mt='md'>
							<Title order={3} mb='sm'>
								Check-Ins Per Hour
							</Title>
							<ResponsiveContainer width='100%' height={250}>
								<LineChart data={stats.checkInsByHour}>
									<XAxis dataKey='label' />
									<YAxis allowDecimals={false} />
									<Tooltip />
									<Line
										type='monotone'
										dataKey='count'
										stroke='#228be6'
										strokeWidth={2}
									/>
								</LineChart>
							</ResponsiveContainer>
						</Paper>
					)}

					<Grid mt='xl'>
						{stats.topUsers?.length > 0 && (
							<Grid.Col xs={12} md={6}>
								<Paper shadow='xs' p='md' withBorder>
									<Title order={4} mb='sm'>
										Top Users
									</Title>
									<Table highlightOnHover>
										<thead>
											<tr>
												<th>Name</th>
												<th>Check-Ins</th>
											</tr>
										</thead>
										<tbody>
											{stats.topUsers?.map((user) => (
												<tr key={user.userId}>
													<td>{user.name}</td>
													<td>{user.count}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</Paper>
							</Grid.Col>
						)}

						{stats.stationProgress?.length > 0 && (
							<Grid.Col xs={12} md={6}>
								<Paper shadow='xs' p='md' withBorder>
									<Title order={4} mb='sm'>
										Station Progress
									</Title>
									<Table highlightOnHover>
										<thead>
											<tr>
												<th>Station</th>
												<th>Completed</th>
												<th>Remaining</th>
											</tr>
										</thead>
										<tbody>
											{stats.stationProgress?.map((s) => (
												<tr key={s.station}>
													<td>{s.stationName}</td>
													<td>{s.completedTotal}</td>
													<td>{s.remainingTotal}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</Paper>
							</Grid.Col>
						)}
					</Grid>

					{stationOptions?.length > 0 && (
						<Paper shadow='xs' p='md' withBorder mt='xl'>
							<Title order={4} mb='sm'>
								Items by Station
							</Title>

							<Select
								label='Select Station'
								data={stationOptions}
								value={selectedStation}
								onChange={setSelectedStation}
								mb='md'
								withinPortal
							/>

							<Table highlightOnHover>
								<thead>
									<tr>
										<th>Item</th>
										<th>Total</th>
										<th>Issued</th>
										<th>Remaining</th>
									</tr>
								</thead>
								<tbody>
									{itemRows.map((item) => (
										<tr key={item.itemName}>
											<td>{item.itemName}</td>
											<td>{item.attached}</td>
											<td>{item.issued}</td>
											<td>{item.remaining}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Paper>
					)}
				</>
			)}
		</Box>
	)
}

export default ExperienceOverviewPage
